import { getEnvironment } from '../utils/helpers';

type RawFormElements = {
  country: HTMLSelectElement | null;
  email: HTMLInputElement | null;
  marketing: HTMLInputElement | null;
  code: HTMLInputElement | null;
  submit: HTMLButtonElement | null;
  errorMsg: HTMLElement | null;
  successMsg: HTMLElement | null;
  noAccountMsg: HTMLElement | null;
}

type SafeFormElements = {
  country: HTMLSelectElement;
  email: HTMLInputElement;
  marketing: HTMLInputElement;
  code: HTMLInputElement;
  submit: HTMLButtonElement;
  errorMsg: HTMLElement;
  successMsg: HTMLElement;
  noAccountMsg: HTMLElement;
}

type SignUpData = {
  user_email: string;
  user_residence_country: string;
  challenge_id: string;
  is_email_opt_in: boolean;
}

type SignupResponse = {
  status: number;
  message: string;
  user_has_account?: boolean;
}

const env = getEnvironment();

function getFormElements(formEl: HTMLFormElement, formContainer: HTMLElement): RawFormElements {
  return {
    country: formEl.querySelector('select'),
    email: formEl.querySelector('input[name="email"]'),
    marketing: formEl.querySelector('input[name="marketing"]'),
    code: formEl.querySelector('input[name="code"]'),
    submit: formEl.querySelector('[data-submit]'),
    errorMsg: formContainer.querySelector('.error-message'),
    successMsg: formContainer.querySelector('.success-message'),
    noAccountMsg: formContainer.querySelector('.success-no-account'),
  }
}

function setupCountryPicker(picker: HTMLSelectElement): void {
  const parentEl = picker.closest('.country-picker')!;

  picker.addEventListener('focus', (): void => {
    parentEl.classList.add('is-open');
  });

  picker.addEventListener('blur', (): void => {
    parentEl.classList.remove('is-open');
  });

  picker.addEventListener('change', (): void => {
    parentEl.classList.remove('is-open');
    picker.blur();
  });

  picker.addEventListener('keydown', (event): void => {
    if (event.key === 'Escape') {
      parentEl.classList.remove('is-open');
    }
  });
}

function setupEmailInput(input: HTMLInputElement): void {
  input.addEventListener('invalid', (): void => {
    if (input.validity.valueMissing) {
      input.setCustomValidity('Please fill out this field.');
    } else if (input.validity.typeMismatch || input.validity.patternMismatch) {
      input.setCustomValidity('Please enter a valid email address.');
    }
  });

  input.addEventListener('input', (): void => {
    input.setCustomValidity('');
  });
}

async function sendSignUp(data: SignUpData): Promise<SignupResponse> {
  const url = `${env.apiBase}/v2/workouts/challenges/enroll_by_email`;

  const request = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (request.status === 400) {
    return {
      status: 400,
      message: 'Please enter a valid email address.',
    }
  }

  const response = await request.json();
  const status = [400].includes(response.status) ? response.status : 201;

  return {
    status,
    message: response.message,
    user_has_account: response.user_has_account,
  }
}

export function initChallengeForm(formContainer: HTMLElement): void {
  const challengeDescription = document.querySelector('.challenge-description');
  const formEl = formContainer.querySelector<HTMLFormElement>('form')!;
  
  if (!formEl) {
    console.error('[pvolve]: Challenge Form - Not found');
    return;
  }

  const rawFormElements = getFormElements(formEl, formContainer);

  const missingElements = Object.entries(rawFormElements)
    .filter(([, el]) => !el)
    .map(([key]) => key);

  if (missingElements.length > 0) {
    console.error('[pvolve]: Challenge Form - Missing required DOM elements:', missingElements);
    return;
  }

  // Doing this because TypeScript and to avoid an convoluted type check hell.
  // And also to avoid using the "magic" ! (non-null assertion).
  const formElements = rawFormElements as SafeFormElements;

  if (formElements.code.dataset.challengeId === 'challenge_id') {
    console.error(
      '[pvolve]: Challenge Form - Default code "challenge_id" found.',
      'Specify an appropriate code.'
    );

    return;
  }

  function showErrorMsg(msg: string): void {
    formElements.errorMsg.querySelector('span')!.textContent = msg;
    formElements.errorMsg.classList.remove('hidden');
  }

  function showSuccessMsg(): void {
    clearErrorMessage();
    challengeDescription?.classList.add('hidden');
    formEl.classList.add('hidden');
    formElements.successMsg.classList.remove('hidden');
  }

  function showNoAccountMsg() {
    clearErrorMessage();
    challengeDescription?.classList.add('hidden');
    formEl.classList.add('hidden');
    formElements.noAccountMsg.classList.remove('hidden');
  }

  function clearErrorMessage(): void {
    formElements.errorMsg.classList.add('hidden');
  }

  function setLoading(loading: boolean): void {
    formElements.submit.disabled = loading;
    formElements.submit.textContent = loading
      ? 'Loading...'
      : formElements.submit.dataset.label ?? 'Sign Up for the Challenge';
  }

  function processResult(result: SignupResponse): void {
    const challengeNotFound = `Current challenge with id ${formElements.code.dataset.challengeId} not found`;
    const accountExists = result.user_has_account;

    if (result.message === challengeNotFound) {
      showErrorMsg(result.message);
      return;
    }

    if (result.status === 400) {
      showErrorMsg(result.message);
      return;
    }

    if (!accountExists) {
      showNoAccountMsg();
      return;
    }

    if (result.status === 201) {
      showSuccessMsg();
    }
  }

  function trackFormSubmitted(): void {
    if (!window.SegmentUser) return;

    const eventName = 'Marketing Opted In';

    void window.SegmentUser.sendSegmentEvent(
      {
        email: formElements.email.value.toLowerCase(),
        email_marketing_opted_in: formElements.marketing.checked
          ? 'opted_in'
          : 'subscribed',
        challenge_country: formElements.country.value,
        challenge_name: formElements.code.dataset.challengeName,
      },
      {
        eventName,
        name: eventName,
        email: formElements.email.value.toLowerCase(),
        is_email_opt_in: formElements.marketing.checked,
        source: window.location.host,
        source_detail: formElements.code.dataset.challengeName,
      }
    );
  }

  setupCountryPicker(formElements.country);
  setupEmailInput(formElements.email);

  formElements.email.addEventListener('keypress', clearErrorMessage);
  formElements.country.addEventListener('change', clearErrorMessage);

  formEl.addEventListener('submit', async (event) => {
    if (formElements.submit.disabled) return;

    event.preventDefault();
    clearErrorMessage();

    setLoading(true);

    const result = await sendSignUp({
      user_email: formElements.email.value.toLowerCase(),
      user_residence_country: formElements.country.value,
      // Casting to avoid warning. Can expand validation logic later.
      challenge_id: formElements.code.dataset.challengeId as string,
      is_email_opt_in: formElements.marketing.checked,
    });

    if (!env.isProduction) {
      console.log('[pvolve]: Challenge Form Result - ', result);
    }

    processResult(result);
    trackFormSubmitted();
    setLoading(false);
  });
}
